import { create } from 'zustand';
import { getAccessTokenByApi } from '@/services/auth';
import { parseAuthToken, IParseJwtReturn } from '@/utils/helpers/token';

type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  accessToken: string;
  tokenData: IParseJwtReturn | null;
  status: AuthStatus;
}

interface AuthActions {
  setAccessToken: (token: string) => void;
  clearAuth: () => void;
  checkAuth: () => Promise<boolean>;
  refreshToken: () => Promise<boolean>;
  getAccessToken: () => string;
  isAuthenticated: () => boolean;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  accessToken: '',
  tokenData: null,
  status: 'idle',
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  setAccessToken: (token: string) => {
    const parsed = parseAuthToken(token);

    if (parsed?.lang) {
      const currentLang = localStorage.getItem('language');
      if (currentLang !== parsed.lang) {
        localStorage.setItem('language', parsed.lang);
      }
    }

    set({
      accessToken: token,
      tokenData: parsed,
      status: parsed?.studentInfo?.studentID
        ? 'authenticated'
        : 'unauthenticated',
    });
  },

  clearAuth: () => {
    set(initialState);
  },

  getAccessToken: () => get().accessToken,

  isAuthenticated: () => {
    const { status, tokenData } = get();
    return status === 'authenticated' && !!tokenData?.studentInfo?.studentID;
  },

  /**
   * REFRESH TOKEN
   * Axios interceptor and other places call this function
   */
  refreshToken: async () => {
    try {
      const resp = await getAccessTokenByApi();
      const token = resp.data?.data?.token;

      if (token) {
        get().setAccessToken(token);
        return get().isAuthenticated();
      }

      set({ status: 'unauthenticated' });
      return false;
    } catch {
      set({ status: 'unauthenticated' });
      return false;
    }
  },

  checkAuth: async () => {
    const { accessToken, tokenData, refreshToken } = get();

    // IF TOKEN EXISTS AND IS VALID, SET AUTHENTICATED
    if (accessToken && tokenData?.studentInfo?.studentID) {
      set({ status: 'authenticated' });
      return true;
    }

    set({ status: 'loading' });

    // IF TOKEN DOES NOT EXIST OR IS INVALID, REFRESH TOKEN
    return refreshToken();
  },
}));
